import { useState, useRef, useCallback } from "react";

/**
 * Drop-in replacement for useState that tracks an undo/redo stack.
 * The setter accepts values or updater functions just like setState.
 */
export function useHistory<T>(initial: T) {
  const [current, setCurrent] = useState(initial);
  const stackRef = useRef<T[]>([initial]);
  const indexRef = useRef(0);
  // Track stack length so we can derive canUndo/canRedo without an extra setState
  const [index, setIndex] = useState(0);
  const [stackLen, setStackLen] = useState(1);

  const set = useCallback((update: T | ((prev: T) => T)) => {
    setCurrent(prev => {
      const next = typeof update === "function" ? (update as (p: T) => T)(prev) : update;
      const s = stackRef.current;
      stackRef.current = [...s.slice(0, indexRef.current + 1), next].slice(-50);
      indexRef.current = stackRef.current.length - 1;
      return next;
    });
    // Batch with setCurrent — React batches these into one render
    setIndex(indexRef.current);
    setStackLen(stackRef.current.length);
  }, []);

  const undo = useCallback(() => {
    if (indexRef.current > 0) {
      indexRef.current -= 1;
      setCurrent(stackRef.current[indexRef.current]);
      setIndex(indexRef.current);
    }
  }, []);

  const redo = useCallback(() => {
    if (indexRef.current < stackRef.current.length - 1) {
      indexRef.current += 1;
      setCurrent(stackRef.current[indexRef.current]);
      setIndex(indexRef.current);
    }
  }, []);

  /** Update current value WITHOUT pushing onto the undo stack.
   *  Use for transient state like loading flags and fetched data. */
  const silent = useCallback((update: T | ((prev: T) => T)) => {
    setCurrent((prev) => {
      const next = typeof update === "function" ? (update as (p: T) => T)(prev) : update;
      // Also patch the current stack entry so undo/redo doesn't resurrect stale data
      stackRef.current[indexRef.current] = next;
      return next;
    });
  }, []);

  const canUndo = index > 0;
  const canRedo = index < stackLen - 1;

  return { current, set, silent, undo, redo, canUndo, canRedo };
}
